export type ClusterFormValue = 'active' | 'inactive';
export const CLUSTER_VALUES = {
  active: 'active' as ClusterFormValue,
  inactive: 'inactive' as ClusterFormValue,
} as const;

export type OriginFormValue = 'commercial' | 'support' | 'financial' | 'other';

export interface SelectOption<T = string> {
  value: T;
  label: string;
  description?: string;
}

// Opções hardcoded em português (mantidas para compatibilidade)
// Use getTranslatedOriginOptions() para obter versões traduzidas
export const ORIGIN_OPTIONS: SelectOption<OriginFormValue>[] = [
  {
    value: 'commercial' as OriginFormValue,
    label: 'Comercial',
    description: 'Atendimento relacionado a vendas e negociações',
  },
  {
    value: 'support' as OriginFormValue,
    label: 'Suporte',
    description: 'Atendimento técnico e suporte ao cliente',
  },
  {
    value: 'financial' as OriginFormValue,
    label: 'Financeiro',
    description: 'Atendimento relacionado a pagamentos e cobranças',
  },
  {
    value: 'other' as OriginFormValue,
    label: 'Outros',
    description: 'Outros tipos de atendimento',
  },
];

export const ORIGIN_VALUES = {
  commercial: 'commercial' as OriginFormValue,
  support: 'support' as OriginFormValue,
  financial: 'financial' as OriginFormValue,
  other: 'other' as OriginFormValue,
} as const;

export type SupportFormValue =
  | 'question'
  | 'complaint'
  | 'suggestion'
  | 'other';

// Opções hardcoded em português (mantidas para compatibilidade)
// Use getTranslatedSupportOptions() para obter versões traduzidas
export const SUPPORT_OPTIONS: SelectOption<SupportFormValue>[] = [
  {
    value: 'question' as SupportFormValue,
    label: 'Dúvida',
    description: 'Dúvida sobre o produto ou serviço',
  },
  {
    value: 'complaint' as SupportFormValue,
    label: 'Reclamação',
    description: 'Reclamação sobre o produto ou serviço',
  },
  {
    value: 'suggestion' as SupportFormValue,
    label: 'Sugestão',
    description: 'Sugestão sobre o produto ou serviço',
  },
  {
    value: 'other' as SupportFormValue,
    label: 'Outros',
    description: 'Outros tipos de suporte',
  },
];

export type FinancialFormValue = 'second_via' | 'cancellation' | 'other';

// Opções hardcoded em português (mantidas para compatibilidade)
// Use getTranslatedFinancialOptions() para obter versões traduzidas
export const FINANCIAL_OPTIONS: SelectOption<FinancialFormValue>[] = [
  {
    value: 'second_via' as FinancialFormValue,
    label: '2ª via',
    description: '2ª via do produto ou serviço',
  },
  {
    value: 'cancellation' as FinancialFormValue,
    label: 'Cancelamento',
    description: 'Cancelamento do produto ou serviço',
  },
  {
    value: 'other' as FinancialFormValue,
    label: 'Outros',
    description: 'Outros tipos de atendimento',
  },
];

export type CommercialFormValue =
  | 'sale_completed'
  | 'under_negotiation'
  | 'lead_lost'
  | 'other';

export const COMMERCIAL_VALUES = {
  sale_completed: 'sale_completed' as CommercialFormValue,
  under_negotiation: 'under_negotiation' as CommercialFormValue,
  lead_lost: 'lead_lost' as CommercialFormValue,
  other: 'other' as CommercialFormValue,
} as const;

// Opções hardcoded em português (mantidas para compatibilidade)
// Use getTranslatedCommercialOptions() para obter versões traduzidas
export const COMMERCIAL_OPTIONS: SelectOption<CommercialFormValue>[] = [
  {
    value: 'sale_completed' as CommercialFormValue,
    label: 'Venda realizada',
    description: 'Venda realizada',
  },
  {
    value: 'under_negotiation' as CommercialFormValue,
    label: 'Em negociação',
    description: 'Em negociação',
  },
  {
    value: 'lead_lost' as CommercialFormValue,
    label: 'Lead perdido',
    description: 'Lead perdido',
  },
  {
    value: 'other' as CommercialFormValue,
    label: 'Outros',
    description: 'Outros tipos de atendimento',
  },
];

export type OrderPaymentFormValue =
  | 'credit'
  | 'debit'
  | 'pix'
  | 'bank_slip'
  | 'cash';

export const ORDER_PAYMENT_VALUES = {
  credit: 'credit' as OrderPaymentFormValue,
  debit: 'debit' as OrderPaymentFormValue,
  pix: 'pix' as OrderPaymentFormValue,
  bank_slip: 'bank_slip' as OrderPaymentFormValue,
  cash: 'cash' as OrderPaymentFormValue,
} as const;

// Opções hardcoded em português (mantidas para compatibilidade)
// Use getTranslatedOrderPaymentOptions() para obter versões traduzidas
export const ORDER_PAYMENT_OPTIONS: SelectOption<OrderPaymentFormValue>[] = [
  {
    value: 'credit' as OrderPaymentFormValue,
    label: 'Cartão de crédito',
    description: 'Cartão de crédito',
  },
  {
    value: 'debit' as OrderPaymentFormValue,
    label: 'Débito',
    description: 'Débito',
  },
  {
    value: 'pix' as OrderPaymentFormValue,
    label: 'Pix',
    description: 'Pix',
  },
  {
    value: 'bank_slip' as OrderPaymentFormValue,
    label: 'Boleto',
    description: 'Boleto',
  },
  {
    value: 'cash' as OrderPaymentFormValue,
    label: 'Dinheiro',
    description: 'Dinheiro',
  },
];

export type OrderStatusFormValue = 'paid' | 'open' | 'canceled';

export const ORDER_STATUS_VALUES = {
  paid: 'paid' as OrderStatusFormValue,
  open: 'open' as OrderStatusFormValue,
  canceled: 'canceled' as OrderStatusFormValue,
} as const;

// Opções hardcoded em português (mantidas para compatibilidade)
// Use getTranslatedOrderStatusOptions() para obter versões traduzidas
export const ORDER_STATUS_OPTIONS: SelectOption<OrderStatusFormValue>[] = [
  {
    value: 'paid' as OrderStatusFormValue,
    label: 'Pago',
    description: 'Pago',
  },
  {
    value: 'open' as OrderStatusFormValue,
    label: 'Em aberto',
    description: 'Em aberto',
  },
  {
    value: 'canceled' as OrderStatusFormValue,
    label: 'Cancelado',
    description: 'Cancelado',
  },
];

export type OrderSaleStatusFormValue = 'one-time-sale' | 'recurring-sale';

export const ORDER_SALES_STATUS_VALUES = {
  'one-time-sale': 'one-time-sale' as OrderSaleStatusFormValue,
  'recurring-sale': 'recurring-sale' as OrderSaleStatusFormValue,
} as const;

// Opções hardcoded em português (mantidas para compatibilidade)
// Use getTranslatedOrderSaleStatusOptions() para obter versões traduzidas
export const ORDER_SALE_STATUS_OPTIONS: SelectOption<OrderSaleStatusFormValue>[] =
  [
    {
      value: 'one-time-sale' as OrderSaleStatusFormValue,
      label: 'Venda única',
      description: 'Venda única',
    },
    {
      value: 'recurring-sale' as OrderSaleStatusFormValue,
      label: 'Venda recorrente',
      description: 'Venda recorrente',
    },
  ];

export interface BotflowFormValue {
  flow_id: string;
}
